// src/pages/FAQSection.jsx (or your components directory)
import React, { useState } from "react";

// Plus Icon
const PlusIcon = () => (
  <svg
    className="w-6 h-6 text-gray-700"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
    ></path>
  </svg>
);

// Minus Icon
const MinusIcon = () => (
  <svg
    className="w-6 h-6 text-gray-700"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M18 12H6"
    ></path>
  </svg>
);

// Accordion Item Component
const AccordionItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <button
        onClick={onClick}
        className="w-full flex justify-between items-center p-5 md:p-6 text-left focus:outline-none"
        aria-expanded={isOpen}
      >
        <span className="text-base md:text-lg font-medium text-gray-800">
          {question}
        </span>
        {isOpen ? <MinusIcon /> : <PlusIcon />}
      </button>
      {isOpen && (
        <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
          <p className="text-gray-600 text-sm md:text-base leading-relaxed">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(1); // Index of the initially open FAQ item (0-based)

  const faqData = [
    {
      question: "How does the Resume builder work?",
      answer:
        "Our Resume builder uses AI to analyze your skills and experience, then suggests relevant content and formatting. You can easily customize layouts, add sections, and tailor your Resume for specific job applications. The process is designed to be intuitive and efficient.",
    },
    {
      question: "Can I download my Resume in different formats?",
      answer:
        "Yes, once you've finished creating your Resume, you can download it in multiple formats, including PDF, Word, and plain text. This allows you to easily share your Resume with potential employers or upload it to job search websites.",
    },
    {
      question: "Can I edit my Resume after downloading it?",
      answer:
        "Absolutely! While our platform offers robust editing tools, you can also download your Resume in editable formats like Word (.docx) to make further changes offline if you prefer. However, we recommend keeping your master Resume updated on our platform for easy access and AI-powered suggestions.",
    },
    {
      question: "Is there a limit to the number of Resumes I can create?",
      answer:
        "This depends on your subscription plan. Our free plan may have a limit, while premium plans typically offer the ability to create and manage multiple Resume versions tailored for different roles or industries. Check our pricing page for detailed information.",
    },
  ];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index); // Toggle: open if closed, close if open
  };

  return (
    // Added id="faqs" here
    <section id="faqs" className="py-16 lg:py-24 bg-white font-poppins">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:space-x-12">
          {/* Left Side: Title, Subtitle, Button */}
          <div className="lg:w-1/3 text-center lg:text-left mb-10 lg:mb-0">
            <h2 className="text-4xl md:text-5xl font-bold text-[#073214] mb-6 leading-tight">
              Answers to Your
              <br />
              Questions
            </h2>
            <p className="text-gray-600 text-base md:text-lg mb-8 leading-relaxed">
              Have questions about using BuildResume? Check out our FAQ section
              for answers to commonly asked questions about our platform,
              features, and more.
            </p>
            <a
              href="#faqs" // Changed to point to the section itself for demo, or could be a different page
              className="inline-block px-8 py-3 border-2 border-gray-300 text-[#073214] font-semibold rounded-full hover:bg-gray-50 hover:border-[#073214] transition-colors"
            >
              Learn More
            </a>
          </div>

          {/* Right Side: Accordion */}
          <div className="lg:w-2/3 space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => handleToggle(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
